import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import DataTable from '../components/DataTable';
import { fetchUsDemographics, fetchRaceSurveyUserCounts } from '../services/api';

const DemographicsDashboard = () => {
    const [demographicsData, setDemographicsData] = useState([]);
    const [userCounts, setUserCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingUserCounts, setLoadingUserCounts] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadDemographicsData = async () => {
            try {
                setLoading(true);
                const response = await fetchUsDemographics();
                
                // Sort by percent_of_total descending
                const sortedData = [...response].sort((a, b) => b.percent_of_total - a.percent_of_total);
                
                setDemographicsData(sortedData);
            } catch (err) {
                console.error('Error loading demographics data:', err);
                setError('Failed to load demographics data');
            } finally {
                setLoading(false);
            }
        };

        const loadUserCounts = async () => {
            try {
                setLoadingUserCounts(true);
                const response = await fetchRaceSurveyUserCounts();
                if (response && response.length > 0) {
                    setUserCounts(response[0]);
                }
            } catch (err) {
                console.error('Error loading user counts:', err);
            } finally {
                setLoadingUserCounts(false);
            }
        };

        loadDemographicsData();
        loadUserCounts();
    }, []);

    // Define table columns matching the LookerStudio table
    // Colors are automatically assigned in order: blue, cyan, pink, orange, yellow, green
    const columns = [
        {
            key: 'identifier_text',
            label: 'Race/Ethnicity Group',
            type: 'text'
        },
        {
            key: 'percent_of_total',
            label: 'Total %',
            type: 'percentage'
            // Auto-assigned: blue (chart palette 1)
        },
        {
            key: 'total_count',
            label: 'Total Respondent #',
            type: 'number'
            // Auto-assigned: cyan (chart palette 2)
        },
        {
            key: 'percent_of_total_yw',
            label: 'YW %',
            type: 'percentage'
            // Auto-assigned: pink (chart palette 3)
        },
        {
            key: 'yw_count',
            label: 'Total YW Respondent #',
            type: 'number'
            // Auto-assigned: orange (chart palette 4)
        },
        {
            key: 'percent_of_total_ed',
            label: 'Educator %',
            type: 'percentage'
            // Auto-assigned: yellow (chart palette 5)
        },
        {
            key: 'ed_count',
            label: 'Total Ed Respondent #',
            type: 'number'
            // Auto-assigned: green (chart palette 6)
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Header pageTitle="U.S. User Demographic Analysis, Historical (Survey Launched on 6/26/24)" />
            
            <div className="px-6 py-6 flex flex-col gap-8">
                {/* Demographics Table */}
                {loading ? (
                    <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
                        <div className="text-lg text-gray-600">Loading demographics data...</div>
                    </div>
                ) : error ? (
                    <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
                        <div className="text-lg text-red-600">Error: {error}</div>
                    </div>
                ) : (
                    <DataTable
                        data={demographicsData}
                        columns={columns}
                        title="US Demographics - Race/Ethnicity Breakdown"
                        rowsPerPage={100}
                        showVisualBars={true}
                    />
                )}

                {/* Scorecards */}
                {loadingUserCounts ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-lg shadow p-6 flex flex-col items-center justify-center">
                                <div className="text-gray-600">Loading...</div>
                            </div>
                        ))}
                    </div>
                ) : userCounts ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Total US Survey Respondents */}
                        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center justify-center">
                            <p className="text-sm text-gray-600 mb-4 text-center">Total # of US Survey Respondents</p>
                            <p className="text-6xl font-bold text-gray-800">
                                {userCounts.all_us_users ? userCounts.all_us_users.toLocaleString() : '--'}
                            </p>
                        </div>

                        {/* US YW Respondents */}
                        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center justify-center">
                            <p className="text-sm text-gray-600 mb-4 text-center"># of US YW Respondents</p>
                            <p className="text-6xl font-bold text-gray-800">
                                {userCounts.all_us_yw_users ? userCounts.all_us_yw_users.toLocaleString() : '--'}
                            </p>
                        </div>

                        {/* US Educator Respondents */}
                        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center justify-center">
                            <p className="text-sm text-gray-600 mb-4 text-center"># of US Educator Respondents</p>
                            <p className="text-6xl font-bold text-gray-800">
                                {userCounts.all_us_ed_users ? userCounts.all_us_ed_users.toLocaleString() : '--'}
                            </p>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default DemographicsDashboard;